import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  LinearProgress,
  MenuItem,
  Rating,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ApartmentIcon from "@mui/icons-material/Apartment";
import HubIcon from "@mui/icons-material/Hub";
import EmergencyShareIcon from "@mui/icons-material/EmergencyShare";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useRecordContext } from "react-admin";
import { useNavigate } from "react-router-dom";
import AssignAgentDialog from "../Common/AssignAgentDialog";
import ChangeCookStatusDialog from "../Common/ChangeCookStatusDialog";
import UpdateCookDetailsDialog from "../Common/UpdateCookDetailsDialog";
import FreshLeadInterface from "../../interfaces/Cook/FreshLeadInterface";
import { CookService } from "../../services/CookServices";
import AddCommentDialogCookProfile from "../Common/AddCommentDialogCookProfile";
import AddChefNoteDialogCookProfile from "../Common/AddChefNoteDialogCookProfile";
import ResourceSelectDialog from "../Common/ResourceSelectDialog";
import HubsList from "../Hubs/HubsList";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { setCustomSelectOpen } from "../../store/reducers/customSelect";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import ModeOfTravel from "../../enums/ModeOfTravelEnum";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";
import { DataGrid, GridValueGetterParams } from "@mui/x-data-grid";
import SkillAssessmentTable from "./SkillAssessmentShow";
import { FreshLeadOnboardingSubStatus, FreshLeadStatus, FreshLeadSubStatusReasoningsForLostLead } from "../../enums/FreshLeadStatus";
import { freshLeadSubStatusReasoningMapFunction, freshLeadSubStatusSubReasoningMapFunction } from "../../utils/FreshLeadStatusMapFunction";
import { setCookProfileData } from "../../store/reducers/cookProfileData";
import { CookWorkStatus } from "../../enums/CookWorkStatus";
import ConfirmationButton from "../Common/ConfirmationButton";
import InventoryKit from "../../interfaces/Cook/InventoryKit";
import { InventoryKitEnum } from "../../enums/InventoryKitEnum";
import GroupPermissionFunction from "../../utils/groupPermissionFunction";
import { UserGroupsEnum } from "../../enums/UserGroupsEnum";
import { CookVerificationStatus } from "../../enums/VerificationStatus";
import MarkNotFreeForWorkDialog from "../Common/MarkNotFreeForWorkDialog";
import { AllCookOnBoardingSubStatus } from "../../enums/AllCookOnboardingSubStatus";
import ChangeCookVerificationAndSeverity from "../Common/ChangeCookVerificationAndSeverity";
import { formatDate, formatDateTimeWithSeconds } from "../../utils/DateTimeUtils";
import { UserType } from "../../enums/common";
import numberToShirtSizeMapping from "../../utils/NumberToShirtSizeMapping";
import CallButton from "../Common/CallButton/CallButton";
import ImagePreviewerDialog from "../Common/ImagePreviewerDialog";
import { ShowLabel } from "../../styles/commonStyles";
import { colors } from "../../styles/colors";
import { LunchPreference } from "../../enums/LunchPreference";
import { CookServiceTypeEnum, CookServiceTypeLabels, toCurrentServiceTypes } from "../../enums/CookServiceTypeEnum";
import { NewVendorSource } from "../../enums/NewVendorSource";
import newVendorSubResource from "../../utils/NewVendorSubResource";
import AdminUserService from "../../services/AdminUser";

const cookService = new CookService();
const adminUserService = new AdminUserService();

// ── Accent colours ─────────────────────────────────────────────────────────────
const A = {
  ratings:    "#1976D2",
  contact:    "#2E7D32",
  emergency:  "#E65100",
  other:      "#6A1B9A",
  status:     "#F57F17",
  cuisine:    "#00695C",
  skills:     "#283593",
  training:   "#4527A0",
  verify:     "#AD1457",
  cluster:    "#00838F",
  activity:   "#4E342E",
  onboarding:  "#1565C0",
  timestamps:  "#455A64",
  retention:   "#00796B",
};

// ── Section title bar ──────────────────────────────────────────────────────────
const SecTitle = ({ label, accent, icon, strip, stripBg, stripOffset, stripMt }: { label: string; accent: string; icon?: React.ReactNode; strip?: boolean; stripBg?: string; stripOffset?: number; stripMt?: number }) => {
  const off = stripOffset ?? 14;
  const mt = stripMt ?? (off - 2);
  return (
    <Box display="flex" alignItems="center" gap={0.75} mb={2} pb={1}
      sx={strip
        ? { borderBottom: "1.5px dashed rgb(210,214,219)", bgcolor: stripBg ?? "#FFF8F2", mx: `-${off}px`, px: `${off}px`, pt: "6px", mt: `-${mt}px`, borderRadius: "9px 9px 0 0" }
        : { borderBottom: `2px solid ${accent}` }
      }>
      {icon && <Box sx={{ color: accent, display: "flex" }}>{icon}</Box>}
      <Typography sx={{ fontSize: "0.8rem", fontWeight: 800, color: accent, textTransform: "uppercase", letterSpacing: "0.6px", filter: strip ? "brightness(0.7)" : "none" }}>
        {label}
      </Typography>
    </Box>
  );
};

// ── Left card row ──────────────────────────────────────────────────────────────
const LRow = ({ label, children, icon }: { label: string; children: React.ReactNode; icon?: React.ReactNode }) => (
  <Box display="flex" alignItems="flex-start" gap={0.5} mb={1.25}>
    <Box display="flex" alignItems="center" gap={0.35} sx={{ minWidth: 100, flexShrink: 0 }}>
      {icon && <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>{icon}</Box>}
      <Typography sx={{ fontSize: "0.74rem", color: colors.light.textGrey, lineHeight: 1.6 }}>
        {label}
      </Typography>
    </Box>
    <Typography sx={{ fontSize: "0.74rem", color: colors.light.textBlack, lineHeight: 1.6, wordBreak: "break-word" }}>
      {children}
    </Typography>
  </Box>
);

// ── Left section box ───────────────────────────────────────────────────────────
const LeftSection = ({ accent, children }: { accent: string; children: React.ReactNode }) => (
  <Box sx={{ border: "1px solid rgb(226,230,233)", borderLeft: `3px solid ${accent}`, borderRadius: "8px", p: "16px 16px 14px", mb: 1.5, bgcolor: "#fff" }}>
    {children}
  </Box>
);

// ── Full-width right card ──────────────────────────────────────────────────────
const RCard = ({ accent, children, bg }: { accent: string; children: React.ReactNode; bg?: string }) => (
  <Box sx={{
    bgcolor: bg ?? "#fff",
    border: "1px solid rgb(226,230,233)",
    borderTop: `3px solid ${accent}99`,
    borderRadius: "12px",
    boxShadow: "0px 1px 4px rgba(0,0,0,0.06)",
    p: "12px 14px",
    mb: 2.5,
    width: "100%",
  }}>
    {children}
  </Box>
);

// ── Read-only compact field ────────────────────────────────────────────────────
const FieldBox = ({ label, value, minW, maxW, wrap }: { label: string; value?: string | null; minW?: number; maxW?: number; wrap?: boolean }) => (
  <Box sx={{ flex: "1 1 140px", minWidth: minW ?? 120, ...(maxW ? { maxWidth: maxW } : {}) }}>
    <ShowLabel style={{ fontSize: "0.63rem", marginBottom: 4 }}>{label}</ShowLabel>
    <Box sx={{
      bgcolor: "rgb(249, 250, 251)",
      border: "1px solid rgb(225, 231, 239)",
      borderRadius: "8px",
      px: 1.5,
      py: "7px",
      fontSize: "0.78rem",
      color: colors.light.textBlack,
      fontWeight: 500,
      minHeight: 34,
      display: "flex",
      alignItems: "center",
      overflow: "hidden",
      whiteSpace: wrap ? "normal" : "nowrap",
      wordBreak: wrap ? "break-word" : undefined,
      textOverflow: wrap ? undefined : "ellipsis",
    }}>
      {value || "—"}
    </Box>
  </Box>
);

// ── Editable field: value inside box + pencil icon → inline select ─────────────
const EditableField = ({
  label, value, options, onSave, accent, minW, maxW, optionLabels, onError, showNone,
}: {
  label: string;
  value: string;
  options: string[];
  onSave: (v: string) => Promise<void>;
  accent: string;
  minW?: number;
  maxW?: number;
  optionLabels?: Record<string, string>;
  onError?: () => void;
  showNone?: boolean;
}) => {
  const [editing, setEditing] = useState(false);
  const [cur, setCur] = useState(value);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setCur(value); }, [value]);

  const handle = async (e: SelectChangeEvent) => {
    const v = e.target.value;
    setBusy(true);
    setEditing(false);
    try {
      await onSave(v);
      setCur(v);
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: `${label} updated`, type: CustomSnackbarTypes.SUCCESS });
    } catch {
      if (onError) {
        onError();
      } else {
        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: `Failed to update ${label}`, type: CustomSnackbarTypes.ERROR });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ flex: "1 1 140px", minWidth: minW ?? 130, ...(maxW ? { maxWidth: maxW } : {}) }}>
      <ShowLabel style={{ fontSize: "0.63rem", marginBottom: 4 }}>{label}</ShowLabel>
      {editing ? (
        <FormControl size="small" fullWidth>
          <Select
            value={cur}
            onChange={handle}
            open
            onClose={() => setEditing(false)}
            displayEmpty
            sx={{
              fontSize: "0.78rem",
              bgcolor: "rgb(249, 250, 251)",
              borderRadius: "8px",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: accent, borderWidth: "1px" },
              "& .MuiSelect-select": { py: "7px", px: "10px" },
            }}
          >
            {showNone && <MenuItem value="" sx={{ fontSize: "0.78rem" }}><em style={{ color: colors.light.textGrey }}>None</em></MenuItem>}
            {options.map((o) => (
              <MenuItem key={o} value={o} sx={{ fontSize: "0.78rem" }}>
                {optionLabels?.[o] ?? o.replace(/_/g, " ")}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <Box
          sx={{
            bgcolor: "rgb(249, 250, 251)",
            border: "1px solid rgb(225, 231, 239)",
            borderRadius: "8px",
            px: 1.5,
            py: "7px",
            minHeight: 34,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            cursor: "pointer",
            "&:hover": { borderColor: accent },
          }}
          onClick={() => setEditing(true)}
        >
          <Typography sx={{ fontSize: "0.78rem", color: colors.light.textBlack, fontWeight: 500, lineHeight: 1.4, flex: 1 }}>
            {cur ? (optionLabels?.[cur] ?? cur.replace(/_/g, " ")) : "—"}
          </Typography>
          {busy
            ? <CircularProgress size={10} sx={{ color: accent, flexShrink: 0 }} />
            : <EditIcon sx={{ fontSize: 11, color: colors.light.textGrey, flexShrink: 0 }} />}
        </Box>
      )}
    </Box>
  );
};

// ── Editable multi-select field: checkboxes inline, saved as one whole list ────
// Unlike EditableField (one value, commits on pick), this holds a set: it commits the whole
// selection when the dropdown closes, matching a backend that replaces the list rather than
// adding/removing one value at a time.
// Stable fallback so EditableMultiField's values-sync effect doesn't re-fire every render.
const NO_SERVICE_TYPES: string[] = [];

const EditableMultiField = ({
  label, values, options, optionLabels, onSave, accent, minW, maxW,
}: {
  label: string;
  values: string[];
  options: string[];
  optionLabels?: Record<string, string>;
  onSave: (v: string[]) => Promise<void>;
  accent: string;
  minW?: number;
  maxW?: number;
}) => {
  const [editing, setEditing] = useState(false);
  const [cur, setCur] = useState<string[]>(values);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setCur(values); }, [values]);

  const label_of = (v: string) => optionLabels?.[v] ?? v.replace(/_/g, " ");
  const sameSet = (a: string[], b: string[]) =>
    a.length === b.length && a.every((v) => b.includes(v));

  const commit = async () => {
    setEditing(false);
    // Nothing ticked or unticked — no need to write.
    if (sameSet(cur, values)) return;
    const next = cur;
    setBusy(true);
    try {
      await onSave(next);
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: `${label} updated`, type: CustomSnackbarTypes.SUCCESS });
    } catch (err: any) {
      setCur(values);
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: err?.response?.data?.message || `Failed to update ${label}`,
        type: CustomSnackbarTypes.ERROR,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ flex: "1 1 140px", minWidth: minW ?? 130, ...(maxW ? { maxWidth: maxW } : {}) }}>
      <ShowLabel style={{ fontSize: "0.63rem", marginBottom: 4 }}>{label}</ShowLabel>
      {editing ? (
        <FormControl size="small" fullWidth>
          <Select
            multiple
            value={cur}
            onChange={(e) => setCur(
              typeof e.target.value === "string" ? e.target.value.split(",") : (e.target.value as string[])
            )}
            open
            onClose={commit}
            displayEmpty
            renderValue={(selected) => (selected as string[]).map(label_of).join(", ") || "—"}
            sx={{
              fontSize: "0.78rem",
              bgcolor: "rgb(249, 250, 251)",
              borderRadius: "8px",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: accent, borderWidth: "1px" },
              "& .MuiSelect-select": { py: "7px", px: "10px" },
            }}
          >
            {options.map((o) => (
              <MenuItem key={o} value={o} sx={{ fontSize: "0.78rem" }}>
                <Checkbox size="small" checked={cur.includes(o)} sx={{ p: 0.5, mr: 1 }} />
                {label_of(o)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <Box
          sx={{
            bgcolor: "rgb(249, 250, 251)",
            border: "1px solid rgb(225, 231, 239)",
            borderRadius: "8px",
            px: 1.5,
            py: "7px",
            minHeight: 34,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            cursor: "pointer",
            "&:hover": { borderColor: accent },
          }}
          onClick={() => setEditing(true)}
        >
          <Typography sx={{ fontSize: "0.78rem", color: colors.light.textBlack, fontWeight: 500, lineHeight: 1.4, flex: 1 }}>
            {cur.length ? cur.map(label_of).join(", ") : "—"}
          </Typography>
          {busy
            ? <CircularProgress size={10} sx={{ color: accent, flexShrink: 0 }} />
            : <EditIcon sx={{ fontSize: 11, color: colors.light.textGrey, flexShrink: 0 }} />}
        </Box>
      )}
    </Box>
  );
};

// ── Editable date field ────────────────────────────────────────────────────────
const EditableDateField = ({
  label, value, onSave, accent, minW, maxW,
}: {
  label: string;
  value: string | null;
  onSave: (v: string | null) => Promise<void>;
  accent: string;
  minW?: number;
  maxW?: number;
}) => {
  const [editing, setEditing] = useState(false);
  const [cur, setCur] = useState(value ?? "");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setCur(value ?? ""); }, [value]);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setBusy(true);
    setEditing(false);
    try {
      await onSave(v || null);
      setCur(v);
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: `${label} updated`, type: CustomSnackbarTypes.SUCCESS });
    } catch {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: `Failed to update ${label}`, type: CustomSnackbarTypes.ERROR });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ flex: "1 1 140px", minWidth: minW ?? 130, ...(maxW ? { maxWidth: maxW } : {}) }}>
      <ShowLabel style={{ fontSize: "0.63rem", marginBottom: 4 }}>{label}</ShowLabel>
      {editing ? (
        <TextField
          type="date"
          value={cur}
          onChange={handleChange}
          onBlur={() => setEditing(false)}
          size="small"
          autoFocus
          fullWidth
          sx={{
            "& .MuiInputBase-input": { fontSize: "0.78rem", py: "7px", px: "10px", bgcolor: "rgb(249, 250, 251)", borderRadius: "8px" },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: accent, borderWidth: "1px" },
          }}
        />
      ) : (
        <Box
          sx={{
            bgcolor: "rgb(249, 250, 251)",
            border: "1px solid rgb(225, 231, 239)",
            borderRadius: "8px",
            px: 1.5,
            py: "7px",
            minHeight: 34,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            cursor: "pointer",
            "&:hover": { borderColor: accent },
          }}
          onClick={() => setEditing(true)}
        >
          <Typography sx={{ fontSize: "0.78rem", color: colors.light.textBlack, fontWeight: 500, lineHeight: 1.4, flex: 1 }}>
            {cur ? formatDate(cur) : "—"}
          </Typography>
          {busy
            ? <CircularProgress size={10} sx={{ color: accent, flexShrink: 0 }} />
            : <EditIcon sx={{ fontSize: 11, color: colors.light.textGrey, flexShrink: 0 }} />}
        </Box>
      )}
    </Box>
  );
};

// ── Score box (cuisine / skills / training) ────────────────────────────────────
const ScoreBox = ({ label, value, outOf = 100 }: { label: string; value?: number | string | null; outOf?: number }) => {
  const score = value != null && value !== "" ? Number(value) : null;
  const rounded = score != null ? Math.round(score) : null;
  const pct = score != null ? Math.min((score / outOf) * 100, 100) : 0;
  const barColor = score == null ? "#BDBDBD" : score >= 70 ? "#2E7D32" : score >= 40 ? "#F57F17" : "#C62828";
  return (
    <Box sx={{ border: "1px solid rgb(226,230,233)", borderRadius: "10px", p: "10px 14px", flex: "1 1 110px", minWidth: 100, bgcolor: "rgb(249,250,251)" }}>
      <ShowLabel style={{ fontSize: "0.63rem", marginBottom: 4 }}>{label}</ShowLabel>
      <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: rounded != null ? barColor : "#BDBDBD" }}>
        {rounded != null ? `${rounded}/${outOf}` : "—"}
      </Typography>
      <LinearProgress variant="determinate" value={pct}
        sx={{ mt: 0.75, height: 4, borderRadius: 3, bgcolor: "rgb(226,230,233)", "& .MuiLinearProgress-bar": { bgcolor: barColor, borderRadius: 3 } }} />
    </Box>
  );
};


// ── Row wrapper ────────────────────────────────────────────────────────────────
const FieldRow = ({ children }: { children: React.ReactNode }) => (
  <Box display="flex" flexWrap="wrap" gap={1} mb={1}>{children}</Box>
);

// ══════════════════════════════════════════════════════════════════════════════

interface InputProps {
  cookId: number;
}

interface KitInterface {
  openRequestKitDialog: boolean;
  setOpenRequestKitDialog: (value: boolean) => void;
  cookId: number;
  refresh: () => void;
  inventoryDetails: InventoryKit[];
}

const alertUser = (message: string) => {
  EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
    message: message,
    type: CustomSnackbarTypes.SUCCESS,
  });
};

interface CustomCookKitFormEntry {
  id?: number;
  inventoryName: string;
  inventoryCount?: number;
}

const CustomCookKitForm = (props: KitInterface) => {
  const [rows, setRows] = useState<CustomCookKitFormEntry[]>([]);
  useEffect(() => {
    const mappedInventories: Array<CustomCookKitFormEntry> = [];
    Object.values(InventoryKitEnum).forEach((value, index) => {
      mappedInventories.push({ id: index, inventoryName: value, inventoryCount: 0 });
    });
    mappedInventories.forEach((inventory) => {
      props?.inventoryDetails?.forEach((inventoryDetail) => {
        if (inventory.inventoryName === inventoryDetail.inventoryName) {
          inventory.inventoryCount = inventoryDetail.inventoryCount;
        }
      });
    });
    setRows(mappedInventories);
  }, [props?.cookId, props?.inventoryDetails]);

  const submitHandler = async () => {
    const mappedInventories: Array<{ inventoryName: string; inventoryCount: number }> = [];
    rows.forEach((key) => {
      mappedInventories.push({ inventoryName: key?.inventoryName, inventoryCount: key?.inventoryCount || 0 });
    });
    const response = await cookService.updateKitDistribution(props.cookId, { inventories: mappedInventories });
    if (response.status === 200) {
      alertUser("Successful");
      setRows([]);
      props.setOpenRequestKitDialog(false);
      props.refresh();
    }
  };

  const columns: any = [
    { field: "inventoryName", headerName: "Name", minWidth: 200, flex: 1 },
    {
      field: "inventoryCount",
      headerName: "Count",
      minWidth: 200,
      flex: 1,
      renderCell: (params: GridValueGetterParams) => (
        <TextField
          variant="outlined"
          type="number"
          value={params?.row?.inventoryCount}
          sx={{ mt: "0px", mb: "0px" }}
          fullWidth
          inputProps={{ min: 0 }}
          onChange={(e) => {
            setRows([...rows].map((row) => {
              if (row.id === params.row.id) {
                return { ...row, inventoryCount: parseInt(e.target.value) };
              }
              return row;
            }));
          }}
        />
      ),
    },
  ];

  if (rows.length === 0) return null;

  return (
    <Box>
      <Dialog open={props.openRequestKitDialog} onClose={() => { props.setOpenRequestKitDialog(false); }}>
        <Box p="1rem">
          <Box sx={{ height: 470, width: "500px" }}>
            <DataGrid
              sx={{ "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": { outline: "none" } }}
              rows={rows}
              columns={columns}
              disableSelectionOnClick
              experimentalFeatures={{ newEditingApi: true }}
              hideFooter={true}
            />
          </Box>
        </Box>
        <DialogActions>
          <Button onClick={() => { props.setOpenRequestKitDialog(false); }} color="primary">Cancel</Button>
          <Button onClick={submitHandler} color="primary">Submit</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ══════════════════════════════════════════════════════════════════════════════

const CookProfile = (props: InputProps) => {
  const [assignAgentDialog, setAgentAssignDialog] = useState<boolean>(false);
  const [changeStatusDialog, setChangeStatusDialog] = useState<boolean>(false);
  const [updateCookProfileDialog, setUpdateCookProfileDialog] = useState<boolean>(false);
  const [addCommentDialog, setAddCommentDialog] = useState<boolean>(false);
  const [addChefNoteDialog, setAddChefNoteDialog] = useState<boolean>(false);
  const [data, setData] = useState<FreshLeadInterface>();
  const [hubList, setHubList] = useState<boolean>(false);
  const [openRequestKitDialog, setOpenRequestKitDialog] = useState<boolean>(false);
  const [removeCookVerificationAndSeverity, setRemoveCookVerificationAndSeverity] = useState<boolean>(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [onboardingProgress, setOnboardingProgress] = useState<any>(null);
  const [retentionFollowup, setRetentionFollowup] = useState<any[]>([]);
  const [showAllComments, setShowAllComments] = useState(false);
  const [editingCommentText, setEditingCommentText] = useState("");
  const [editCommentDialogOpen, setEditCommentDialogOpen] = useState<boolean>(false);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [freeToWorkOpen, setFreeToWorkOpen] = useState(false);
  const [freeToWorkComment, setFreeToWorkComment] = useState('');
  const [markNotFreeOpen, setMarkNotFreeOpen] = useState(false);
  const [owners, setOwners] = useState<any[]>([]);
  const [ownerEditing, setOwnerEditing] = useState(false);
  const [ownerBusy, setOwnerBusy] = useState(false);

  const record = useRecordContext();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userDetails = useAppSelector((state) => state.userDetails.value);
  let groupsArray: string[] = userDetails.adminUser.accessGroups;

  const handleHubListDialogOpen = () => {
    setHubList(true);
    dispatch(setCustomSelectOpen({ open: true }));
  };

  const applyProfileData = (profileData: any) => {
    setData(profileData);
    dispatch(setCookProfileData(profileData));
    if (profileData?.onboardingProgress) {
      setOnboardingProgress(profileData.onboardingProgress);
    }
    if (Array.isArray(profileData?.retentionFollowUps)) {
      setRetentionFollowup(profileData.retentionFollowUps);
    }
  };

  const refresh = () => {
    cookService.getCookByLead(props?.cookId)
      .then((res) => { applyProfileData(res.data); })
      .catch((err) => { console.log(err); });
  };

  const handleCreateWebsiteUrl = async (cookId: number) => {
    try {
      await cookService.patchCookProfileSlug(cookId);
      refresh();
    } catch (err) { console.log(err); }
  };

  useEffect(() => {
    cookService.getCookByLead(props?.cookId)
      .then((res) => { applyProfileData(res.data); })
      .catch((err) => { console.log(err); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, props?.cookId, assignAgentDialog, changeStatusDialog, addCommentDialog, updateCookProfileDialog]);

  useEffect(() => {
    adminUserService.getAllAdminUsers(1000, 0, {}, {}).then((res) => {
      setOwners((res.data || []).map((u: any) => ({
        adminId: u.id,
        adminDetails: { id: u.id, name: u.name, email: u.email },
      })));
    }).catch(console.log);
  }, []);

  if (!data) return <></>;

  const overall = record?.cookFullProfile?.overallCustomerRating;
  const actual  = record?.cookFullProfile?.actualCustomerRating;
  const expYears = data?.cookingSince ? new Date().getFullYear() - new Date(data.cookingSince).getFullYear() : null;

  const patchStatus = (partial: Partial<{ onboardingSubStatus: string; subStatusReasoning: string; subStatusSubReasoning: string }>) =>
    cookService.editCookProfileStatus(data.cookId, {
      onboardingSubStatus: data.onboardingSubStatus ?? "",
      subStatusReasoning: data.subStatusReasoning ?? "",
      subStatusSubReasoning: data.subStatusSubReasoning ?? "",
      comment: "",
      ...partial,
    }).then(() => refresh());

  const patchProfile = (partial: Record<string, unknown>) =>
    cookService.editCookProfile(data.cookId, partial).then(() => refresh());

  const handleWorkStatusSave = async (v: string): Promise<void> => {
    if (v === CookWorkStatus.FREE_TO_WORK) {
      if (data?.workStatus === CookWorkStatus.BLACKLISTED) {
        setFreeToWorkOpen(true);
        throw new Error('needs_comment');
      }
      await cookService.markFreeToWork(props.cookId, { comment: '' });
      refresh();
      return;
    }
    setMarkNotFreeOpen(true);
    throw new Error('needs_reason');
  };

  const saveEditComment = async (commentId: number) => {
    try {
      await cookService.editComment(props.cookId, commentId, { comment: editingCommentText });
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: "Comment updated", type: CustomSnackbarTypes.SUCCESS });
      setEditCommentDialogOpen(false);
      setEditingCommentId(null);
      refresh();
    } catch {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: "Failed to update comment", type: CustomSnackbarTypes.ERROR });
    }
  };

  const patchOnboarding = async (partial: Record<string, any>) => {
    const updated = await cookService.updateOnboardingProgress(data.cookId, {
      leadDate: onboardingProgress?.leadDate ?? null,
      interestedInWorkingWithCk: onboardingProgress?.interestedInWorkingWithCk ?? null,
      whenToCallAgain: onboardingProgress?.whenToCallAgain ?? null,
      interestedInEngagement: onboardingProgress?.interestedInEngagement ?? null,
      interestedServiceType: onboardingProgress?.interestedServiceType ?? null,
      officeVisitDate: onboardingProgress?.officeVisitDate ?? null,
      rescheduledVisitDate: onboardingProgress?.rescheduledVisitDate ?? null,
      canJoinFrom: onboardingProgress?.canJoinFrom ?? null,
      joinedOn: onboardingProgress?.joinedOn ?? null,
      processPending: onboardingProgress?.processPending ?? null,
      ...partial,
    });
    setOnboardingProgress(updated);
  };

  return (
    <div>
      {/* ── Action buttons ── */}
      <Stack direction="row" spacing={2}>
        <Grid container spacing={2} justifyContent="flex-end">
          {
            GroupPermissionFunction(groupsArray, [UserGroupsEnum.ADMIN, UserGroupsEnum.MANAGER]) &&
            ([CookVerificationStatus.REPORT_GENERATED].includes(data?.verificationStatus)) &&
            (data?.verificationReportSeverity !== "Clear") &&
            (
              <Grid item>
                <ConfirmationButton
                  buttonLabel="Mark Authbridge Report Clear"
                  onSuccess={async () => {
                    const response = await cookService.overrideAuthBridge(props.cookId);
                    if (response.status === 200) { alertUser("Successful"); }
                    refresh();
                  }}
                  title="Are you sure ?"
                  buttonProps={{ color: "secondary" }}
                />
              </Grid>
            )
          }
          <Grid item>
            <Button onClick={() => { setRemoveCookVerificationAndSeverity(true); }} color={"secondary"} style={{ width: "max-content", fontSize: "12px" }} size="small" variant="text">
              Change Cook Verification And Severity
            </Button>
          </Grid>
          <Grid item>
            <Button onClick={() => { setAgentAssignDialog(true); }} color={"secondary"} style={{ width: "max-content", fontSize: "12px" }} size="small" variant="text">
              Assign Agent
            </Button>
          </Grid>
          <Grid item>
            <Button onClick={() => { setChangeStatusDialog(true); }} color={"secondary"} style={{ width: "max-content", fontSize: "12px" }} size="small" variant="text">
              Change Status
            </Button>
          </Grid>
          <Grid item>
            <Button onClick={() => { setAddCommentDialog(true); }} color={"secondary"} style={{ width: "max-content", fontSize: "12px" }} size="small" variant="text">
              Add Comment
            </Button>
          </Grid>
          <Grid item>
            <Button onClick={() => { setAddChefNoteDialog(true); }} color={"secondary"} style={{ width: "max-content", fontSize: "12px" }} size="small" variant="text">
              Add Chef Note
            </Button>
          </Grid>
          {GroupPermissionFunction(groupsArray, [UserGroupsEnum.ADMIN]) &&
            <Grid item>
              <Button
                onClick={() => { handleCreateWebsiteUrl(props?.cookId); }}
                disabled={[AllCookOnBoardingSubStatus.COMPLETE].includes(data?.onboardingSubStatus) && [CookWorkStatus.FREE_TO_WORK].includes(data?.workStatus)}
                size="small" variant="text" color={"secondary"} style={{ width: "max-content", fontSize: "12px" }}
              >
                Quick onboard
              </Button>
            </Grid>
          }
          <Grid item>
            <Button onClick={() => { handleHubListDialogOpen(); }} size="small" variant="text" color={"secondary"} style={{ width: "max-content", fontSize: "12px" }}>
              Update Hub
            </Button>
          </Grid>
          {[FreshLeadStatus.SKILL_ASSESSMENT].includes(data?.onboardingStatus) && (
            <Grid item>
              <ConfirmationButton
                buttonLabel="Assessment Complete"
                onSuccess={async () => {
                  const response = await cookService.markAssessmentComplete(props.cookId);
                  if (response.status === 200) { alertUser("Successful"); }
                  refresh();
                }}
                title="Are you sure ?"
                buttonProps={{ color: "secondary" }}
              />
            </Grid>
          )}
          {[FreshLeadStatus.APPROVAL_CHECKS].includes(data?.onboardingStatus) && (
            <Grid item>
              <ConfirmationButton
                buttonLabel="Approval Done"
                onSuccess={async () => {
                  const response = await cookService.markApprovalVerification(props.cookId);
                  if (response.status === 200) { alertUser("Successful"); }
                  refresh();
                }}
                title="Are you sure ?"
                buttonProps={{ color: "secondary" }}
              />
            </Grid>
          )}
          {[FreshLeadStatus.TRAINING].includes(data?.onboardingStatus) && (data?.trainingRating?.appTraining >= 0) && (
            <Grid item>
              <ConfirmationButton
                buttonLabel="Training Complete"
                onSuccess={async () => {
                  const response = await cookService.markTrainingComplete(props.cookId);
                  if (response.status === 200) { alertUser("Successful"); }
                  refresh();
                }}
                title="Are you sure ?"
                buttonProps={{ color: "secondary" }}
              />
            </Grid>
          )}
          <Grid item>
            <ConfirmationButton
              buttonLabel="Force Logout"
              onSuccess={async () => {
                const response = await cookService.forceLogoutCook(props.cookId);
                if (response.status === 200) { alertUser("Cook logged out successfully"); }
                refresh();
              }}
              title="Force logout this cook from all devices?"
              buttonProps={{ color: "error" }}
            />
          </Grid>
          {[FreshLeadStatus.ONBOARDING].includes(data?.onboardingStatus) && [CookWorkStatus.ONBOARDING_INCOMPLETE].includes(data?.workStatus) && (
            <Grid item>
              <ConfirmationButton
                buttonLabel="Onboarding Complete"
                onSuccess={async () => {
                  const response = await cookService.markOnboardingComplete(props.cookId);
                  if (response.status === 200) { alertUser("Successful"); }
                  refresh();
                }}
                title="Are you sure ?"
                buttonProps={{ color: "secondary" }}
              />
            </Grid>
          )}
          {[FreshLeadStatus.ONBOARDING].includes(data?.onboardingStatus) &&
            <Grid item>
              <Button variant="text" onClick={() => { setOpenRequestKitDialog(true); }} size="small" color={"secondary"} style={{ width: "max-content", fontSize: "12px" }}>
                Request Kit
              </Button>
            </Grid>
          }
          <Grid item display={"flex"} gap={2}>
            {data?.onboardingStatus === FreshLeadStatus.FRESH_LEAD ? (
              <Button onClick={() => { setUpdateCookProfileDialog(true); }} size="small" color="error" variant="contained">Add Profile</Button>
            ) : (
              <Button onClick={() => { setUpdateCookProfileDialog(true); }} size="small" color="error" variant="contained">Update Profile</Button>
            )}
            {GroupPermissionFunction(groupsArray, [UserGroupsEnum.ADMIN]) && (
              <Button
                onClick={() => {
                  EventBus.emitEvent(EventBusEventsEnum.SHOW_CONFIRMATION_DIALOG, {
                    message: "Are you sure you want to delete skill assessment for this cook",
                    onClose: (confirmed: boolean) => {
                      if (confirmed) {
                        cookService.deleteSkillAssessmentRecord(props.cookId)
                          .then(() => {
                            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: "Deleted Successfully", type: CustomSnackbarTypes.SUCCESS });
                            window.location.reload();
                          })
                          .catch(() => {
                            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: "Error Deleting", type: CustomSnackbarTypes.ERROR });
                          });
                      }
                    },
                    title: "Confirmation",
                  });
                }}
                size="small" color="error" variant="contained"
              >
                Delete Skill Assessment
              </Button>
            )}
          </Grid>
        </Grid>
      </Stack>

      {/* ── Dialogs ── */}
      <CustomCookKitForm
        cookId={props.cookId}
        refresh={refresh}
        inventoryDetails={data.kitDistributionCount as InventoryKit[]}
        openRequestKitDialog={openRequestKitDialog}
        setOpenRequestKitDialog={setOpenRequestKitDialog}
      />
      <AssignAgentDialog cookDetail={data as FreshLeadInterface} isOpen={assignAgentDialog} setIsOpen={setAgentAssignDialog} refresh={refresh} />
      <ChangeCookStatusDialog cookDetail={data as FreshLeadInterface} isOpen={changeStatusDialog} setIsOpen={setChangeStatusDialog} refresh={refresh} />
      <UpdateCookDetailsDialog
        cookDetail={data as FreshLeadInterface}
        isOpen={updateCookProfileDialog}
        setIsOpen={setUpdateCookProfileDialog}
        isFirstTimeRequest={data.onboardingStatus === FreshLeadStatus.FRESH_LEAD ? true : false}
        refresh={refresh}
      />
      <AddCommentDialogCookProfile cookDetail={data as FreshLeadInterface} isOpen={addCommentDialog} setIsOpen={setAddCommentDialog} refresh={refresh} />
      <AddChefNoteDialogCookProfile cookDetail={data as FreshLeadInterface} isOpen={addChefNoteDialog} setIsOpen={setAddChefNoteDialog} refresh={refresh} />
      <ChangeCookVerificationAndSeverity cookDetail={data as FreshLeadInterface} isOpen={removeCookVerificationAndSeverity} setIsOpen={setRemoveCookVerificationAndSeverity} refresh={refresh} />

      {/* ════════════ PROFILE LAYOUT ════════════ */}
      <Box display="flex" gap={2} alignItems="flex-start" sx={{ mt: 1 }}>

        {/* ════════════ LEFT STICKY PANEL ════════════ */}
        <Box sx={{
          width: 248,
          minWidth: 248,
          flexShrink: 0,
          position: "sticky",
          top: 60,
          alignSelf: "flex-start",
          bgcolor: "#fff",
          border: "1px solid rgb(226,230,233)",
          borderRadius: "12px",
          boxShadow: "0px 1px 4px rgba(0,0,0,0.07)",
          overflow: "visible",
        }}>
          {/* Header */}
          <Box sx={{ bgcolor: "rgb(248,249,252)", borderBottom: "1px solid rgb(226,230,233)", p: "12px 14px 10px", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Box sx={{ cursor: "pointer", mb: 0.6 }} onClick={() => setPhotoOpen(true)}>
              {data?.profilePhoto
                ? <Avatar src={`${data.profilePhoto}?t=${Date.now()}`} sx={{ width: 110, height: 110, border: "2px solid rgb(226,230,233)" }} />
                : <Avatar sx={{ width: 110, height: 110, bgcolor: "rgb(226,230,233)" }}><PersonIcon sx={{ fontSize: 52, color: colors.light.textGrey }} /></Avatar>}
            </Box>
            <Typography sx={{ color: colors.light.textBlack, fontWeight: 700, fontSize: "0.9rem", textAlign: "center", textTransform: "capitalize" }}>
              {data?.firstName} {data?.lastName}
            </Typography>
            <Typography sx={{ fontSize: "0.95rem", color: colors.light.textBlack, fontFamily: "monospace", fontWeight: 700, mt: 0.5 }}>
              Cook ID: {record?.id}
            </Typography>
            <Chip
              label={
                data?.onboardingStatus === FreshLeadStatus.ONBOARDING &&
                data?.onboardingSubStatus === AllCookOnBoardingSubStatus.COMPLETE &&
                (data?.workStatus === CookWorkStatus.FREE_TO_WORK || data?.workStatus === CookWorkStatus.RE_TRAINING)
                  ? "Active"
                  : "Inactive"
              }
              color={
                data?.onboardingStatus === FreshLeadStatus.ONBOARDING &&
                data?.onboardingSubStatus === AllCookOnBoardingSubStatus.COMPLETE &&
                (data?.workStatus === CookWorkStatus.FREE_TO_WORK || data?.workStatus === CookWorkStatus.RE_TRAINING)
                  ? "success"
                  : "default"
              }
              size="small"
              sx={{ mt: 0.5, fontSize: "0.65rem", height: 20, fontWeight: 700 }}
            />
          </Box>

          {/* Sections */}
          <Box p="10px 12px 14px">

            <LeftSection accent={A.ratings}>
              <SecTitle label="Ratings" accent={A.ratings} icon={<span style={{ fontSize: 11 }}>⭐</span>} strip stripBg="#EBF3FD" stripOffset={16} stripMt={16} />
              {[
                { label: "Cx Overall",       value: overall?.overallRating,      by: overall?.noOfPeopleRated },
                { label: "Cx Chefit",        value: overall?.chefitRating,       by: overall?.chefitRatingCount },
                { label: "Cx Party",         value: overall?.chefForPartyRating, by: overall?.chefForPartyRatingCount },
                { label: "Actual Overall",   value: actual?.overallRating,       by: actual?.noOfPeopleRated },
                { label: "Actual Chefit",    value: actual?.chefitRating,        by: actual?.chefitRatingCount },
                { label: "Actual Party",     value: actual?.chefForPartyRating,  by: actual?.chefForPartyRatingCount },
              ].map(({ label, value, by }) => (
                <Tooltip key={label} title={`Rated by ${by ?? 0} people`} arrow placement="right">
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.25}>
                    <Typography sx={{ fontSize: "0.8rem", color: colors.light.textGrey, flex: 1 }}>{label}</Typography>
                    <Box display="flex" alignItems="center" gap={0.6} flexShrink={0}>
                      <Rating value={Number(value ?? 0)} readOnly precision={0.5} size="medium"
                        sx={{ "& .MuiRating-icon": { width: 18 }, fontSize: "1.1rem" }} />
                      <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: colors.light.textBlack, minWidth: 28, textAlign: "right" }}>
                        {value != null ? Number(Number(value).toFixed(1)) : "–"}
                      </Typography>
                    </Box>
                  </Box>
                </Tooltip>
              ))}
              <Box display="flex" justifyContent="space-between" alignItems="center" pt={0.5} mt={0.25} sx={{ borderTop: "1px dashed rgb(226,230,233)" }}>
                <Typography sx={{ fontSize: "0.7rem", color: colors.light.textGrey }}>Tier</Typography>
                <Chip label={data?.tier?.replace(/_/g, " ") ?? "N/A"} size="small"
                  sx={{ fontSize: "0.63rem", height: 20, bgcolor: A.ratings, color: "#fff", fontWeight: 700 }} />
              </Box>
            </LeftSection>

            <LeftSection accent={A.contact}>
              <SecTitle label="Contact Details" accent={A.contact} icon={<PhoneIphoneIcon sx={{ fontSize: 12 }} />} strip stripBg="#F0F7F0" stripOffset={16} stripMt={16} />
              <LRow label="Mobile No." icon={<PhoneIphoneIcon sx={{ fontSize: 10, color: A.contact }} />}>
                <CallButton toPhoneNumber={record?.mobile} userType={UserType.COOK} referenceId={Number(record?.id)} referenceModelName="COOK_FULL_PROFILE" disableMask={true} />
              </LRow>
              <LRow label="WhatsApp No." icon={<WhatsAppIcon sx={{ fontSize: 10, color: "#25D366" }} />}>{data?.whatsappNumber ?? "—"}</LRow>
              <LRow label="Current City" icon={<LocationOnIcon sx={{ fontSize: 10, color: A.contact }} />}>{data?.currentAddressCity ?? "—"}</LRow>
              <LRow label="Preferred Cities" icon={<ApartmentIcon sx={{ fontSize: 10, color: A.contact }} />}>
                {Array.isArray(record?.cookFullProfile?.preferredCities)
                  ? record.cookFullProfile.preferredCities.join(", ")
                  : record?.cookFullProfile?.preferredCities ?? "—"}
              </LRow>
              <LRow label="Chef Hub Name" icon={<HubIcon sx={{ fontSize: 10, color: A.contact }} />}>{data?.hubDetails?.name ?? "—"}</LRow>
            </LeftSection>

            <LeftSection accent={A.emergency}>
              <SecTitle label="Emergency Contact" accent={A.emergency} icon={<EmergencyShareIcon sx={{ fontSize: 12 }} />} strip stripBg="#FFF3EE" stripOffset={16} stripMt={16} />
              <LRow label="Name">{data?.emergencyContactName ?? "—"}</LRow>
              <LRow label="Relation">{data?.emergencyContactRelation ?? "—"}</LRow>
              <LRow label="Phone No.">
                {data?.emergencyContactNumber
                  ? <CallButton toPhoneNumber={data.emergencyContactNumber} userType={UserType.COOK} referenceId={Number(record?.id)} referenceModelName="COOK_FULL_PROFILE" disableMask={true} />
                  : "—"}
              </LRow>
            </LeftSection>

            <LeftSection accent={A.other}>
              <SecTitle label="Other Details" accent={A.other} icon={<span style={{ fontSize: 11 }}>📋</span>} strip stripBg="#F7EEF9" stripOffset={16} stripMt={16} />
              <LRow label="Religion">{data?.religion ?? "—"}</LRow>
              <LRow label="Gender">{data?.gender ?? "—"}</LRow>
              <LRow label="DOB">{data?.dateOfBirth ? formatDate(data.dateOfBirth) : "—"}</LRow>
              <LRow label="Cooking Since">{data?.cookingSince ? new Date(data.cookingSince).getFullYear().toString() : "—"}</LRow>
              <LRow label="Experience">{expYears != null ? `${expYears} yrs` : "—"}</LRow>
              <LRow label="Languages">{Array.isArray(data?.languages) ? data.languages.join(", ") : data?.languages ?? "—"}</LRow>
              <LRow label="Vaccination">{data?.vaccinationStatus ?? "—"}</LRow>
              <LRow label="Smartphone">{data?.hasSmartphone ? "Yes" : "No"}</LRow>
              <LRow label="Has Bike">{data?.modeOfTravel ? (data.modeOfTravel === ModeOfTravel.BIKE ? "Yes" : "No") : "—"}</LRow>
              <LRow label="T-Shirt Size">{data?.tShirtSize ? numberToShirtSizeMapping(data.tShirtSize) : "—"}</LRow>
            </LeftSection>

            <Box mt={2}>
              <LeftSection accent={A.timestamps}>
                <SecTitle label="Timestamps" accent={A.timestamps} icon={<span style={{ fontSize: 11 }}>🕐</span>} strip stripBg="#EEF1F2" stripOffset={16} stripMt={16} />
                <Box sx={{ maxHeight: 328, overflowY: "auto", pr: 0.5 }}>
                  <LRow label="Created At">{(data as any)?.createdAt ? formatDateTimeWithSeconds((data as any).createdAt) : "—"}</LRow>
                  <LRow label="Updated At">{(data as any)?.updatedAt ? formatDateTimeWithSeconds((data as any).updatedAt) : "—"}</LRow>
                  <LRow label="Onboarded At">{(data as any)?.onboardedAt ? formatDateTimeWithSeconds((data as any).onboardedAt) : "—"}</LRow>
                  <LRow label="Profile Created At">{(data as any)?.profileCreatedAt ? formatDateTimeWithSeconds((data as any).profileCreatedAt) : "—"}</LRow>
                  <LRow label="Kit Last Activity">{(data as any)?.kitLastActivityAt ? formatDateTimeWithSeconds((data as any).kitLastActivityAt) : "—"}</LRow>
                  <LRow label="Reactivated At">{(data as any)?.reactivatedAt ? formatDateTimeWithSeconds((data as any).reactivatedAt) : "—"}</LRow>
                  <LRow label="Last Activated">{data?.lastActivatedAt ? formatDateTimeWithSeconds(data.lastActivatedAt as any) : "—"}</LRow>
                  <LRow label="Address Updated">{data?.addressUpdatedAt ? formatDateTimeWithSeconds(data.addressUpdatedAt as any) : "—"}</LRow>
                  <LRow label="Reasoning At">{data?.cookWorkStatusReasoningUpdatedAt ? formatDateTimeWithSeconds(data.cookWorkStatusReasoningUpdatedAt as any) : "—"}</LRow>
                  <LRow label="Last App Activity">{record?.lastAppActivityAt ? formatDateTimeWithSeconds(record.lastAppActivityAt) : "—"}</LRow>
                  <LRow label="Last Visit Activity">{(data as any)?.lastVisitActivityAt ? formatDateTimeWithSeconds((data as any).lastVisitActivityAt) : "—"}</LRow>
                  <LRow label="Slots (System)">{record?.lastSlotsUpdatedBySystemAt ? formatDateTimeWithSeconds(record.lastSlotsUpdatedBySystemAt) : "—"}</LRow>
                </Box>
              </LeftSection>
            </Box>
          </Box>
        </Box>

        {/* ════════════ RIGHT SCROLL PANEL ════════════ */}
        <Box flex={1} sx={{ minWidth: 0 }}>

          {/* 1. Profile Status */}
          <RCard accent={A.status}>
            {/* Title row with Cook Profile button on the right */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} pb={1} sx={{ borderBottom: "1.5px dashed rgb(210,214,219)", bgcolor: "#FFF8F2", mx: "-14px", px: "14px", pt: "4px", mt: "-12px", borderRadius: "9px 9px 0 0" }}>
              <Box display="flex" alignItems="center" gap={0.75}>
                <span style={{ fontSize: 13 }}>🔄</span>
                <Typography sx={{ fontSize: "0.8rem", fontWeight: 800, color: A.status, textTransform: "uppercase", letterSpacing: "0.6px", filter: "brightness(0.7)" }}>
                  Profile Status
                </Typography>
              </Box>
              {record?.slug && (
                <Box
                  component="a"
                  href={`https://thechefkart.com/cooks/${record.slug}/profile`}
                  target="_blank"
                  rel="noreferrer"
                  sx={{
                    display: "flex", alignItems: "center", gap: 0.5,
                    bgcolor: A.status, border: `1.5px solid ${A.status}`,
                    borderRadius: "8px", px: 1.25, py: "5px",
                    textDecoration: "none", cursor: "pointer",
                    "&:hover": { opacity: 0.88 },
                  }}
                >
                  <Typography sx={{ fontSize: "0.72rem", color: "#fff", fontWeight: 700 }}>Cook Profile</Typography>
                  <OpenInNewIcon sx={{ fontSize: 11, color: "#fff" }} />
                </Box>
              )}
            </Box>
            <FieldRow>
              <FieldBox label="Onboarding Status" value={data?.onboardingStatus?.replace(/_/g, " ")} />
              <EditableField label="Onboarding SubStatus" value={data?.onboardingSubStatus ?? ""} options={Object.values(AllCookOnBoardingSubStatus)}
                onSave={(v) => patchStatus({ onboardingSubStatus: v, subStatusReasoning: "", subStatusSubReasoning: "" })} accent={A.status} />
              <EditableField
                label="Work Status"
                value={data?.workStatus ?? ""}
                options={[CookWorkStatus.FREE_TO_WORK, CookWorkStatus.NOT_FREE_TO_WORK]}
                optionLabels={{ [CookWorkStatus.FREE_TO_WORK]: "Mark Free To Work", [CookWorkStatus.NOT_FREE_TO_WORK]: "Mark Not Free To Work" }}
                onSave={handleWorkStatusSave}
                onError={() => {}}
                accent={A.status}
              />
              <FieldBox label="Work Status Reason" value={data?.cookWorkStatusReasoning?.replace(/_/g, " ") ?? undefined} />
              {data?.onboardingSubStatus === AllCookOnBoardingSubStatus.COMPLETE ? (
                <FieldBox label="OBStatus Reason" value={data?.subStatusReasoning ?? undefined} minW={100} maxW={180} />
              ) : (
                <EditableField label="OBStatus Reason" value={data?.subStatusReasoning ?? ""}
                  options={freshLeadSubStatusReasoningMapFunction(data?.onboardingSubStatus as unknown as FreshLeadOnboardingSubStatus) ?? []}
                  onSave={(v) => patchStatus({ subStatusReasoning: v })} accent={A.status} minW={100} maxW={180} showNone />
              )}
              {data?.onboardingSubStatus === AllCookOnBoardingSubStatus.COMPLETE ? (
                <FieldBox label="OBStatus SubReason" value={data?.subStatusSubReasoning ?? undefined} minW={100} maxW={180} />
              ) : (
                <EditableField label="OBStatus SubReason" value={data?.subStatusSubReasoning ?? ""}
                  options={freshLeadSubStatusSubReasoningMapFunction(data?.subStatusReasoning as FreshLeadSubStatusReasoningsForLostLead) ?? []}
                  onSave={(v) => patchStatus({ subStatusSubReasoning: v })} accent={A.status} minW={100} maxW={180} showNone />
              )}
              <FieldBox label="Is MG Cook" value={data?.isMgCook ? "Yes" : "No"} minW={80} maxW={100} />
              {/* The separate MG Tag field is gone — its values are part of Cook Type List now.
                  Edited in place as checkboxes and saved as one whole list, so a cook can hold
                  any combination; the previous single-select wrote [oneValue], silently
                  discarding every other service the cook worked. Retired values stored on the
                  row (CHEFIT_CHEF / PARTY_CHEF) are shown ticked as their replacement, so a
                  save normalises them instead of dropping the capability. */}
              <EditableMultiField label="Cook Type List"
                values={toCurrentServiceTypes(data?.cookTypeList)}
                options={Object.values(CookServiceTypeEnum)}
                optionLabels={CookServiceTypeLabels}
                onSave={(v) => cookService
                  .setCookTypeList(data.cookId, { cookTypeList: v as CookServiceTypeEnum[] })
                  .then(() => { refresh(); })}
                accent={A.status} minW={180} />
              <EditableField label="Cooks Non-Veg?" value={data?.cooksNonVeg ? "YES" : "NO"} options={["YES","NO"]}
                onSave={(v) => patchProfile({ cooksNonVeg: v === "YES" })} accent={A.status} minW={90} maxW={130} />
              <EditableField label="Lunch Preference" value={data?.lunchPreference ?? ""} options={Object.values(LunchPreference)}
                onSave={(v) => patchProfile({ lunchPreference: v })} accent={A.status} maxW={180} />
            </FieldRow>
          </RCard>

          {/* 2. Cuisine Ratings */}
          <RCard accent={A.cuisine}>
            <SecTitle label="Cuisine Ratings" accent={A.cuisine} icon={<span style={{ fontSize: 13 }}>🍽️</span>} strip stripBg="#F0FFF4" />
            <FieldRow>
              <ScoreBox label="North Indian"  value={data?.northIndianFinalRating} />
              <ScoreBox label="South Indian"  value={data?.southIndianFinalRating} />
              <ScoreBox label="Chinese"       value={data?.chineseDishFinalRating} />
              <ScoreBox label="Continental"   value={data?.continentalDishFinalRating} />
              <ScoreBox label="Mexican"       value={data?.mexicanDishFinalRating} />
              <ScoreBox label="Thai"          value={data?.thaiDishFinalRating} />
            </FieldRow>
          </RCard>

          {/* 3. Soft Skills Ratings */}
          <RCard accent={A.skills}>
            <SecTitle label="Soft Skills Ratings" accent={A.skills} icon={<span style={{ fontSize: 13 }}>🧠</span>} strip stripBg="#ECEEF9" />
            <FieldRow>
              <ScoreBox label="Communication"            value={data?.softSkillsRating?.communication} />
              <ScoreBox label="Hygiene"                  value={data?.softSkillsRating?.hygiene} />
              <ScoreBox label="Behaviour"                value={data?.softSkillsRating?.behaviour} />
              <ScoreBox label="Regularity & Punctuality" value={data?.softSkillsRating?.regularityAndPunctuality} />
            </FieldRow>
          </RCard>

          {/* 4. Training Scores */}
          <RCard accent={A.training}>
            <SecTitle label="Training Scores" accent={A.training} icon={<span style={{ fontSize: 13 }}>🎓</span>} strip stripBg="#EDE8F9" />
            <FieldRow>
              <ScoreBox label="App Training"         value={data?.trainingRating?.appTraining} />
              <ScoreBox label="Communication"        value={data?.trainingRating?.communications} />
              <ScoreBox label="Hygiene Training"     value={data?.trainingRating?.hygiene} />
              <ScoreBox label="Kitchen Etiquette"    value={data?.trainingRating?.kitchenEtiquette} />
              <ScoreBox label="Situational"          value={data?.trainingRating?.situational} />
              <Box sx={{ flex: "1 1 100px", minWidth: 90, border: `2px solid ${A.training}44`, borderRadius: "10px", p: "10px 14px", bgcolor: `${A.training}08` }}>
                <ShowLabel style={{ fontSize: "0.63rem", marginBottom: 4 }}>Final Score</ShowLabel>
                <Typography sx={{ fontSize: "1.2rem", fontWeight: 800, color: A.training }}>{data?.trainingRating?.finalTrainingScore != null ? Math.round(Number(data.trainingRating.finalTrainingScore)) : "—"}</Typography>
              </Box>
            </FieldRow>
          </RCard>

          {/* 5. Verification Details */}
          <RCard accent={A.verify}>
            {/* Title row with Initiate Verification button on the right */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} pb={1} sx={{ borderBottom: "1.5px dashed rgb(210,214,219)", bgcolor: "#FDEBF3", mx: "-14px", px: "14px", pt: "4px", mt: "-12px", borderRadius: "9px 9px 0 0" }}>
              <Box display="flex" alignItems="center" gap={0.75}>
                <span style={{ fontSize: 13 }}>🔐</span>
                <Typography sx={{ fontSize: "0.8rem", fontWeight: 800, color: A.verify, textTransform: "uppercase", letterSpacing: "0.6px", filter: "brightness(0.7)" }}>
                  Verification Details
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex", alignItems: "center", gap: 0.5,
                  bgcolor: A.verify, border: `1.5px solid ${A.verify}`,
                  borderRadius: "8px", px: 1.25, py: "5px",
                  cursor: "pointer", "&:hover": { opacity: 0.88 },
                }}
                onClick={() => {
                  EventBus.emitEvent(EventBusEventsEnum.SHOW_CONFIRMATION_DIALOG, {
                    message: "Are you sure you want to initiate verification?",
                    onClose: async (confirmed: boolean) => {
                      if (confirmed) {
                        const response = await cookService.markAuthbridgeVerification(props.cookId);
                        if (response.status === 200) { alertUser("Successful"); }
                        refresh();
                      }
                    },
                    title: "Are you sure ?",
                  });
                }}
              >
                <VerifiedUserIcon sx={{ fontSize: 12, color: "#fff" }} />
                <Typography sx={{ fontSize: "0.72rem", color: "#fff", fontWeight: 700 }}>Initiate Verification</Typography>
              </Box>
            </Box>

            <FieldRow>
              <FieldBox label="Aadhar No."         value={data?.aadharNumber} />
              <FieldBox label="Father's Name"       value={data?.fathersName} />
              <FieldBox label="Report Status"        value={data?.verificationStatus?.replace(/_/g, " ")} />
              <Box sx={{ flex: "1 1 140px", minWidth: 120 }}>
                <ShowLabel style={{ fontSize: "0.63rem", marginBottom: 4 }}>Verification Status</ShowLabel>
                <Box sx={{
                  bgcolor: "rgb(249, 250, 251)",
                  border: "1px solid rgb(225, 231, 239)",
                  borderRadius: "8px",
                  px: 1.5,
                  py: "7px",
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  minHeight: 34,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  color: data?.verificationReportSeverity === "Clear"
                    ? "#2E7D32"
                    : data?.verificationReportSeverity === "Failed"
                    ? "#C62828"
                    : data?.verificationReportSeverity
                    ? "#F57F17"
                    : colors.light.textBlack,
                }}>
                  {data?.verificationReportSeverity && (
                    <Box component="span" sx={{
                      width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                      bgcolor: data.verificationReportSeverity === "Clear"
                        ? "#2E7D32"
                        : data.verificationReportSeverity === "Failed"
                        ? "#C62828"
                        : "#F57F17",
                    }} />
                  )}
                  {data?.verificationReportSeverity || "—"}
                </Box>
              </Box>

              {/* Verification Report URL */}
              <Box sx={{ flex: "1 1 120px", minWidth: 100, maxWidth: 180 }}>
                <ShowLabel style={{ fontSize: "0.63rem", marginBottom: 4 }}>Verification Report</ShowLabel>
                <Box
                  sx={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    bgcolor: data?.verificationReportUrl ? `${A.verify}12` : "rgb(249,250,251)",
                    border: data?.verificationReportUrl ? `1.5px solid ${A.verify}55` : "1px solid rgb(225,231,239)",
                    borderRadius: "8px", px: 1.5, py: "7px", minHeight: 34,
                    cursor: data?.verificationReportUrl ? "pointer" : "default",
                    "&:hover": data?.verificationReportUrl ? { borderColor: A.verify, bgcolor: `${A.verify}18` } : {},
                  }}
                  onClick={() => { if (data?.verificationReportUrl) window.open(data.verificationReportUrl, "_blank", "noopener,noreferrer"); }}
                >
                  <Typography sx={{ fontSize: "0.78rem", color: data?.verificationReportUrl ? A.verify : "#BDBDBD", fontWeight: 700 }}>
                    {data?.verificationReportUrl ? "View Report" : "—"}
                  </Typography>
                  {data?.verificationReportUrl && <OpenInNewIcon sx={{ fontSize: 12, color: A.verify }} />}
                </Box>
              </Box>

              {/* Aadhar Front — window.open, no download */}
              {data?.aadharFrontPhoto && (
                <Box sx={{ flex: "1 1 120px", minWidth: 100, maxWidth: 180 }}>
                  <ShowLabel style={{ fontSize: "0.63rem", marginBottom: 4 }}>Aadhar Front</ShowLabel>
                  <Box
                    sx={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      bgcolor: `${A.verify}12`, border: `1.5px solid ${A.verify}55`,
                      borderRadius: "8px", px: 1.5, py: "7px", minHeight: 34,
                      cursor: "pointer", "&:hover": { borderColor: A.verify, bgcolor: `${A.verify}18` },
                    }}
                    onClick={() => window.open(data.aadharFrontPhoto, "_blank", "noopener,noreferrer")}
                  >
                    <Typography sx={{ fontSize: "0.78rem", color: A.verify, fontWeight: 700 }}>View</Typography>
                    <OpenInNewIcon sx={{ fontSize: 12, color: A.verify }} />
                  </Box>
                </Box>
              )}

              {/* Aadhar Back — window.open, no download */}
              {data?.aadharBackPhoto && (
                <Box sx={{ flex: "1 1 120px", minWidth: 100, maxWidth: 180 }}>
                  <ShowLabel style={{ fontSize: "0.63rem", marginBottom: 4 }}>Aadhar Back</ShowLabel>
                  <Box
                    sx={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      bgcolor: `${A.verify}12`, border: `1.5px solid ${A.verify}55`,
                      borderRadius: "8px", px: 1.5, py: "7px", minHeight: 34,
                      cursor: "pointer", "&:hover": { borderColor: A.verify, bgcolor: `${A.verify}18` },
                    }}
                    onClick={() => window.open(data.aadharBackPhoto, "_blank", "noopener,noreferrer")}
                  >
                    <Typography sx={{ fontSize: "0.78rem", color: A.verify, fontWeight: 700 }}>View</Typography>
                    <OpenInNewIcon sx={{ fontSize: 12, color: A.verify }} />
                  </Box>
                </Box>
              )}
            </FieldRow>

            {/* Aadhar Address — shrinks to content width */}
            <Box mt={1} sx={{ display: "inline-block", maxWidth: "100%" }}>
              <ShowLabel style={{ fontSize: "0.63rem", marginBottom: 4 }}>Full address as per Aadhaar</ShowLabel>
              <Box sx={{
                bgcolor: "rgb(249, 250, 251)", border: "1px solid rgb(225, 231, 239)",
                borderRadius: "8px", px: 1.5, py: "7px", fontSize: "0.78rem",
                color: "#546E7A", fontWeight: 500, minHeight: 34, display: "inline-flex", alignItems: "center",
                maxWidth: "100%",
              }}>
                {[data?.permanentAddressLine1, data?.permanentAddressLine2, data?.permanentAddressState, data?.permanentAddressPinCode].filter(Boolean).join(", ") || "—"}
              </Box>
            </Box>
          </RCard>

          {/* 6. Cluster Details */}
          <RCard accent={A.cluster}>
            <SecTitle label="Cluster Details" accent={A.cluster} icon={<span style={{ fontSize: 13 }}>📍</span>} strip stripBg="#E8F7F8" />
            <FieldRow>
              <FieldBox label="Clusters Mapped" wrap
                value={Array.isArray((data as any)?.clustersMapped) && (data as any).clustersMapped.length > 0
                  ? (data as any).clustersMapped.map((c: any) => c.name).join(", ")
                  : null} />
              <FieldBox label="Chef Hub"      value={String(data?.chefHub ?? "—")} />
              <FieldBox label="Chef Hub Name"  value={data?.hubDetails?.name ?? null} />
              <FieldBox label="Chef Hub Sector" value={(data?.hubDetails as any)?.areaName ?? null} />
              <FieldBox label="Latitude"      value={data?.currentAddCoordinates?.coordinates?.[1] != null ? String(data.currentAddCoordinates!.coordinates![1]) : null} />
            </FieldRow>
            {/* Longitude + Current Address — same line */}
            <Box mt={1} display="flex" gap={1.5} alignItems="flex-start">
              <Box sx={{ flexShrink: 0 }}>
                <ShowLabel style={{ fontSize: "0.63rem", marginBottom: 4 }}>Longitude</ShowLabel>
                <Box sx={{
                  bgcolor: "rgb(249, 250, 251)", border: "1px solid rgb(225, 231, 239)",
                  borderRadius: "8px", px: 1.5, py: "7px", fontSize: "0.78rem",
                  color: "#546E7A", fontWeight: 500, minHeight: 34, display: "inline-flex", alignItems: "center",
                }}>
                  {data?.currentAddCoordinates?.coordinates?.[0] != null ? String(data.currentAddCoordinates!.coordinates![0]) : "—"}
                </Box>
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <ShowLabel style={{ fontSize: "0.63rem", marginBottom: 4 }}>Current Address</ShowLabel>
                <Box sx={{
                  bgcolor: "rgb(249, 250, 251)", border: "1px solid rgb(225, 231, 239)",
                  borderRadius: "8px", px: 1.5, py: "7px", fontSize: "0.78rem",
                  color: "#546E7A", fontWeight: 500, minHeight: 34, display: "inline-flex", alignItems: "center",
                }}>
                  {[data?.currentAddressLine1, data?.currentAddressLine2, data?.currentAddressCity, data?.currentAddressState, data?.currentAddressPinCode].filter(Boolean).join(", ") || "—"}
                </Box>
              </Box>
            </Box>
          </RCard>

          {/* 7. Last Activated At */}
          <RCard accent={A.activity}>
            <SecTitle label="Last Activated At" accent={A.activity} icon={<span style={{ fontSize: 13 }}>🕐</span>} strip stripBg="#F7F2F1" />
            <FieldRow>
              <FieldBox label="Last Visit Activity" value={(data as any)?.lastVisitActivityAt ? formatDate((data as any).lastVisitActivityAt) : null} />
              <FieldBox label="Last App Activity"   value={(data as any)?.lastAppActivity ? formatDate((data as any).lastAppActivity) : null} />
              <FieldBox label="Last Activated at"   value={data?.lastActivatedAt ? formatDate(data.lastActivatedAt) : null} />
            </FieldRow>
          </RCard>

          {/* 8 + 9. Comments & Kit Distribution — side by side */}
          <Box display="flex" gap={1.5} alignItems="flex-start" mb={2.5}>

            {/* Comments */}
            <Box sx={{ flex: 3, minWidth: 0 }}>
              <Box sx={{ bgcolor: "#fff", border: "1px solid rgb(226,230,233)", borderTop: "3px solid #546E7A99", borderRadius: "12px", boxShadow: "0px 1px 4px rgba(0,0,0,0.06)", p: "12px 14px" }}>
                {/* Header */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} pb={1} sx={{ borderBottom: "1.5px dashed rgb(210,214,219)", bgcolor: "#ECEFF1", mx: "-14px", px: "14px", pt: "6px", mt: "-12px", borderRadius: "9px 9px 0 0", flexShrink: 0 }}>
                  <Box display="flex" alignItems="center" gap={0.75}>
                    <span style={{ fontSize: 13 }}>💬</span>
                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 800, color: "#37474F", textTransform: "uppercase", letterSpacing: "0.6px", filter: "brightness(0.7)" }}>Comments</Typography>
                  </Box>
                  <Tooltip title="Add Comment" arrow>
                    <Box sx={{ cursor: "pointer", color: "#546E7A", display: "flex" }} onClick={() => setAddCommentDialog(true)}>
                      <AddCircleOutlineIcon sx={{ fontSize: 18 }} />
                    </Box>
                  </Tooltip>
                </Box>

                {data?.comments && data.comments.length > 0 ? (() => {
                  const kitCount = Object.values(InventoryKitEnum).length - 2;
                  const sorted = [...data.comments].reverse();
                  const visible = sorted.slice(0, kitCount);
                  const hiddenCount = sorted.length - kitCount;

                  const CommentRow = ({ row, i, isLatest }: { row: any; i: number; isLatest?: boolean }) => (
                    <TableRow key={i} sx={{ "&:last-child td": { border: 0 } }}>
                      <TableCell sx={{ fontSize: "0.74rem", whiteSpace: "nowrap" }}>{row.name ?? "—"}</TableCell>
                      <TableCell sx={{ fontSize: "0.74rem", whiteSpace: "normal", wordBreak: "break-word", maxWidth: 260 }}>
                        {row.comment ?? "—"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.74rem", whiteSpace: "nowrap" }}>{row.timestamp ? formatDate(row.timestamp) : "—"}</TableCell>
                      <TableCell sx={{ width: 28, px: 0.5 }}>
                        {isLatest && (
                          <Tooltip title="Edit" arrow>
                            <Box sx={{ cursor: "pointer", color: colors.light.textGrey, display: "flex" }}
                              onClick={() => { setEditingCommentId(row.id); setEditingCommentText(row.comment ?? ""); setEditCommentDialogOpen(true); }}>
                              <EditIcon sx={{ fontSize: 13 }} />
                            </Box>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );

                  return (
                    <>
                      <Box sx={{ overflowX: "auto", border: "1px solid rgb(226,230,233)", borderRadius: "8px", flex: 1 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: "rgb(249,250,251)" }}>
                              {["Owner", "Comment", "Timestamp", ""].map((h, idx) => (
                                <TableCell key={idx} sx={{ fontSize: "0.7rem", fontWeight: 700, color: colors.light.textGrey, whiteSpace: "nowrap" }}>{h}</TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {visible.map((row: any, i: number) => <CommentRow key={i} row={row} i={i} isLatest={i === 0} />)}
                          </TableBody>
                        </Table>
                      </Box>
                      {hiddenCount > 0 && (
                        <Box display="flex" justifyContent="flex-end" mt={1} sx={{ flexShrink: 0 }}>
                          <Button size="small" variant="outlined" onClick={() => setShowAllComments(true)}
                            sx={{ fontSize: "0.72rem", textTransform: "none", borderColor: "#546E7A", color: "#546E7A", "&:hover": { borderColor: "#546E7A", bgcolor: "#546E7A14" } }}>
                            View more ({hiddenCount} more)
                          </Button>
                        </Box>
                      )}
                      <Dialog open={showAllComments} onClose={() => { setShowAllComments(false); }} maxWidth="md" fullWidth>
                        <DialogTitle sx={{ fontSize: "0.9rem", fontWeight: 700, pb: 1 }}>All Comments ({data.comments.length})</DialogTitle>
                        <DialogContent sx={{ p: 0 }}>
                          <Box sx={{ overflowX: "auto" }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ bgcolor: "rgb(249,250,251)" }}>
                                  {["Owner", "Comment", "Timestamp", ""].map((h, idx) => (
                                    <TableCell key={idx} sx={{ fontSize: "0.7rem", fontWeight: 700, color: colors.light.textGrey, whiteSpace: "nowrap" }}>{h}</TableCell>
                                  ))}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {sorted.map((row: any, i: number) => <CommentRow key={i} row={row} i={i} isLatest={i === 0} />)}
                              </TableBody>
                            </Table>
                          </Box>
                        </DialogContent>
                      </Dialog>

                      {/* Edit Comment Dialog */}
                      <Dialog open={editCommentDialogOpen} onClose={() => { setEditCommentDialogOpen(false); setEditingCommentId(null); }} maxWidth="sm" fullWidth>
                        <DialogTitle sx={{ fontSize: "0.95rem", fontWeight: 700, pb: 1 }}>Edit Comment</DialogTitle>
                        <DialogContent sx={{ pt: "8px !important" }}>
                          <TextField
                            value={editingCommentText}
                            onChange={(e) => setEditingCommentText(e.target.value)}
                            multiline
                            minRows={3}
                            fullWidth
                            autoFocus
                            size="small"
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !e.shiftKey && editingCommentId !== null) saveEditComment(editingCommentId);
                            }}
                            sx={{ "& .MuiInputBase-input": { fontSize: "0.84rem" } }}
                          />
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 2 }}>
                          <Button size="small" onClick={() => { setEditCommentDialogOpen(false); setEditingCommentId(null); }}
                            sx={{ fontSize: "0.78rem", color: colors.light.textGrey }}>Cancel</Button>
                          <Button size="small" variant="contained" onClick={() => { if (editingCommentId !== null) saveEditComment(editingCommentId); }}
                            sx={{ fontSize: "0.78rem", bgcolor: "#546E7A", "&:hover": { bgcolor: "#455A64" } }}>Save</Button>
                        </DialogActions>
                      </Dialog>
                    </>
                  );
                })() : (
                  <Typography sx={{ fontSize: "0.78rem", color: colors.light.textGrey }}>No comments yet.</Typography>
                )}
              </Box>
            </Box>

            {/* Kit Distribution — always shown; 0 counts when no data */}
            <Box sx={{ flex: 2, minWidth: 0 }}>
              <Box sx={{ bgcolor: "#fff", border: "1px solid rgb(226,230,233)", borderTop: "3px solid #37474F99", borderRadius: "12px", boxShadow: "0px 1px 4px rgba(0,0,0,0.06)", p: "12px 14px" }}>
                <SecTitle label="Kit Distribution List" accent="#37474F" icon={<span style={{ fontSize: 13 }}>📦</span>} strip stripBg="#EEF1F2" />
                <Box sx={{ overflowX: "auto", border: "1px solid rgb(226,230,233)", borderRadius: "8px" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "rgb(249,250,251)" }}>
                        {["Name", "Count"].map((h) => (
                          <TableCell key={h} sx={{ fontSize: "0.7rem", fontWeight: 700, color: colors.light.textGrey }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.values(InventoryKitEnum).map((inventoryName) => {
                        const found = (data?.kitDistributionCount as any[] ?? []).find((r: any) => r.inventoryName === inventoryName);
                        return (
                          <TableRow key={inventoryName} sx={{ "&:last-child td": { border: 0 } }}>
                            <TableCell sx={{ fontSize: "0.74rem" }}>{inventoryName}</TableCell>
                            <TableCell sx={{ fontSize: "0.74rem" }}>{found?.inventoryCount ?? 0}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            </Box>

          </Box>

        </Box>
      </Box>

      {/* ── Onboarding Progress — full width ── */}
      <Box sx={{ bgcolor: "#fff", border: "1px solid rgb(226,230,233)", borderTop: `3px solid ${A.onboarding}99`, borderRadius: "12px", boxShadow: "0px 1px 4px rgba(0,0,0,0.06)", p: "12px 14px", mt: 2.5, mb: 2.5 }}>
        <SecTitle label="Onboarding Progress" accent={A.onboarding} icon={<span style={{ fontSize: 13 }}>🚀</span>} strip stripBg="#EAF2FF" />
        {/* Single auto-fill grid — column count fixed by width, partial rows don't expand items */}
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(165px, 1fr))", gap: 1 }}>
          <EditableDateField label="Intrested Date" value={onboardingProgress?.leadDate ?? null}
            onSave={(v) => patchOnboarding({ leadDate: v })} accent={A.onboarding} />
          <EditableField label="Interested in Working with CK?" value={onboardingProgress?.interestedInWorkingWithCk != null ? (onboardingProgress.interestedInWorkingWithCk ? "YES" : "NO") : ""}
            options={["YES", "NO"]} onSave={(v) => patchOnboarding({ interestedInWorkingWithCk: v === "YES" })} accent={A.onboarding} />
          <EditableDateField label="When to Call Again" value={onboardingProgress?.whenToCallAgain ?? null}
            onSave={(v) => patchOnboarding({ whenToCallAgain: v })} accent={A.onboarding} />
          <EditableField label="Interested in PT/MG?" value={onboardingProgress?.interestedInEngagement ?? ""}
            options={["MG", "PART_TIME"]} onSave={(v) => patchOnboarding({ interestedInEngagement: v })} accent={A.onboarding} />
          <EditableMultiField label="Interested Service Type" values={onboardingProgress?.interestedServiceType ?? NO_SERVICE_TYPES}
            options={["CHEFIT", "PARTY", "ROYALE"]} optionLabels={{ CHEFIT: "Chefit", PARTY: "Party", ROYALE: "Royal" }}
            onSave={(v) => patchOnboarding({ interestedServiceType: v })} accent={A.onboarding} />
          <FieldBox label="Total DNP" value={onboardingProgress?.totalDnp != null ? String(onboardingProgress.totalDnp) : null} />
          <FieldBox label="Total DNC" value={onboardingProgress?.totalDnc != null ? String(onboardingProgress.totalDnc) : null} />
          <FieldBox label="Previous Salary" value={data?.currentMonthlyEarnings != null ? String(data.currentMonthlyEarnings) : null} />
          <FieldBox label="Offered Per Day Payout" value={data?.offeredSalary != null ? String(data.offeredSalary) : null} />
          <FieldBox label="Offered Per Day Conveyance" value={data?.offeredPerDayConveyance != null ? String(data.offeredPerDayConveyance) : null} />
          {/* Owner Name editable dropdown */}
          <Box>
            <ShowLabel style={{ fontSize: "0.63rem", marginBottom: 4 }}>Owner Name</ShowLabel>
            {ownerEditing ? (
              <FormControl size="small" fullWidth>
                <Select
                  value={data?.owner ? String(data.owner) : ""}
                  onChange={(e: SelectChangeEvent) => {
                    const adminId = Number(e.target.value);
                    setOwnerEditing(false);
                    setOwnerBusy(true);
                    cookService.updateOwner(props.cookId, { authUserId: adminId })
                      .then(() => {
                        refresh();
                        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: "Owner updated", type: CustomSnackbarTypes.SUCCESS });
                      })
                      .catch(() => {
                        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: "Failed to update owner", type: CustomSnackbarTypes.ERROR });
                      })
                      .finally(() => setOwnerBusy(false));
                  }}
                  open
                  onClose={() => setOwnerEditing(false)}
                  displayEmpty
                  sx={{
                    fontSize: "0.78rem",
                    bgcolor: "rgb(249, 250, 251)",
                    borderRadius: "8px",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: A.onboarding, borderWidth: "1px" },
                    "& .MuiSelect-select": { py: "7px", px: "10px" },
                  }}
                >
                  <MenuItem value="" sx={{ fontSize: "0.78rem" }}><em style={{ color: colors.light.textGrey }}>None</em></MenuItem>
                  {owners
                    .filter((o, idx, self) =>
                      self.findIndex((s) => s.adminDetails.name === o.adminDetails.name) === idx &&
                      ["Vikas", "Abhishek", "Kuldeep", "Munish", "Sandeep"].some((n) =>
                        o.adminDetails.name?.toLowerCase().startsWith(n.toLowerCase())
                      )
                    )
                    .map((o) => (
                      <MenuItem key={o.adminId} value={String(o.adminId)} sx={{ fontSize: "0.78rem" }}>{o.adminDetails.name}</MenuItem>
                    ))}
                </Select>
              </FormControl>
            ) : (
              <Box
                sx={{
                  bgcolor: "rgb(249, 250, 251)",
                  border: "1px solid rgb(225, 231, 239)",
                  borderRadius: "8px",
                  px: 1.5,
                  py: "7px",
                  minHeight: 34,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  cursor: "pointer",
                  "&:hover": { borderColor: A.onboarding },
                }}
                onClick={() => setOwnerEditing(true)}
              >
                <Typography sx={{ fontSize: "0.78rem", color: colors.light.textBlack, fontWeight: 500, lineHeight: 1.4, flex: 1 }}>
                  {owners.find((o) => o.adminId === data?.owner)?.adminDetails?.name ?? data?.ownerEmail ?? "—"}
                </Typography>
                {ownerBusy
                  ? <CircularProgress size={10} sx={{ color: A.onboarding, flexShrink: 0 }} />
                  : <EditIcon sx={{ fontSize: 11, color: colors.light.textGrey, flexShrink: 0 }} />}
              </Box>
            )}
          </Box>
          <FieldBox label="Created At" value={(data as any)?.profileCreatedAt ? formatDate((data as any).profileCreatedAt) : null} />
          <EditableDateField label="Office Visit Date" value={onboardingProgress?.officeVisitDate ?? null}
            onSave={(v) => patchOnboarding({ officeVisitDate: v })} accent={A.onboarding} />
          <EditableDateField label="Rescheduled Visit Date" value={onboardingProgress?.rescheduledVisitDate ?? null}
            onSave={async (v) => {
              await patchOnboarding({ rescheduledVisitDate: v });
              refresh();
            }} accent={A.onboarding} />
          <EditableDateField label="Can Join From" value={onboardingProgress?.canJoinFrom ?? null}
            onSave={(v) => patchOnboarding({ canJoinFrom: v })} accent={A.onboarding} />
          <EditableDateField label="Joined On" value={onboardingProgress?.joinedOn ?? null}
            onSave={(v) => patchOnboarding({ joinedOn: v })} accent={A.onboarding} />
          <EditableField label="Process Completed" value={onboardingProgress?.processPending ?? ""}
            options={["FIRST_DAY", "SECOND_DAY"]} onSave={(v) => patchOnboarding({ processPending: v })} accent={A.onboarding} />
          {/* Clicking the referral number navigates to that chef's profile */}
          <Box sx={{ flex: "1 1 140px", minWidth: 120 }}>
            <ShowLabel style={{ fontSize: "0.63rem", marginBottom: 4 }}>Referral Mobile No.</ShowLabel>
            <Box
              sx={{
                bgcolor: "rgb(249, 250, 251)",
                border: "1px solid rgb(225, 231, 239)",
                borderRadius: "8px",
                px: 1.5,
                py: "7px",
                fontSize: "0.78rem",
                color: data?.referralMobileNumber ? A.onboarding : colors.light.textBlack,
                fontWeight: data?.referralMobileNumber ? 600 : 500,
                minHeight: 34,
                display: "flex",
                alignItems: "center",
                cursor: data?.referralMobileNumber ? "pointer" : "default",
                textDecoration: data?.referralMobileNumber ? "underline" : "none",
                "&:hover": data?.referralMobileNumber ? { borderColor: A.onboarding, bgcolor: `${A.onboarding}0d` } : {},
              }}
              onClick={async () => {
                if (!data?.referralMobileNumber) return;
                try {
                  const result = await cookService.getAllCooks(1, 0, { mobile: data.referralMobileNumber });
                  if (result.data && result.data.length > 0) {
                    navigate(`/Cooks/${result.data[0].id}/show`);
                  } else {
                    EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: "No chef found with this referral number", type: CustomSnackbarTypes.ERROR });
                  }
                } catch {
                  EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: "Failed to find chef profile", type: CustomSnackbarTypes.ERROR });
                }
              }}
            >
              {data?.referralMobileNumber || "—"}
            </Box>
          </Box>
          <EditableField label="Source" value={data?.source ?? ""}
            options={Object.values(NewVendorSource)} onSave={(v) => patchProfile({ source: v })} accent={A.onboarding} />
          <EditableField label="Sub Source" value={data?.subSource ?? ""}
            options={newVendorSubResource(data?.source ?? "") ?? []} onSave={(v) => patchProfile({ subSource: v })} accent={A.onboarding} />
          <EditableField label="Interested in Household Job"
            value={data?.interestedInHouseholdJobs != null ? (data.interestedInHouseholdJobs ? "YES" : "NO") : ""}
            options={["YES", "NO"]} onSave={(v) => patchProfile({ interestedInHouseholdJobs: v === "YES" })} accent={A.onboarding} />
          <EditableField label="Campaign Name" value={(data as any)?.campaignName ?? ""}
            options={["Lead From"]} onSave={(v) => patchProfile({ campaignName: v })} accent={A.onboarding} />
        </Box>
      </Box>

      {/* ── Retention Follow-up — full width, horizontal ── */}
      <Box sx={{ bgcolor: "#fff", border: "1px solid rgb(226,230,233)", borderTop: `3px solid ${A.retention}99`, borderRadius: "12px", boxShadow: "0px 1px 4px rgba(0,0,0,0.06)", p: "12px 14px", mb: 2.5 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} pb={1} sx={{ borderBottom: "1.5px dashed rgb(210,214,219)", bgcolor: "#FFF8E1", mx: "-14px", px: "14px", pt: "6px", mt: "-12px", borderRadius: "9px 9px 0 0" }}>
          <Box display="flex" alignItems="center" gap={0.75}>
            <span style={{ fontSize: 13 }}>✅</span>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 800, color: A.retention, textTransform: "uppercase", letterSpacing: "0.6px", filter: "brightness(0.7)" }}>
              Retention Follow-up
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex", alignItems: "center", gap: 0.5,
              bgcolor: A.retention, border: `1.5px solid ${A.retention}`,
              borderRadius: "8px", px: 1.25, py: "5px",
              cursor: "pointer", "&:hover": { opacity: 0.88 },
            }}
            onClick={() => {
              EventBus.emitEvent(EventBusEventsEnum.SHOW_CONFIRMATION_DIALOG, {
                message: "Are you sure you want to reset all retention follow-up entries?",
                onClose: async (confirmed: boolean) => {
                  if (confirmed) {
                    try {
                      const days = ["DAY_1","DAY_2","DAY_3","DAY_4","DAY_5","DAY_6","DAY_7"];
                      await Promise.all(days.map((day) => cookService.updateRetentionFollowup(props.cookId, { day, isCompleted: false })));
                      setRetentionFollowup([]);
                      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: "Retention follow-up reset", type: CustomSnackbarTypes.SUCCESS });
                    } catch {
                      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: "Failed to reset retention follow-up", type: CustomSnackbarTypes.ERROR });
                    }
                  }
                },
                title: "Reset Retention Follow-up",
              });
            }}
          >
                <RestartAltIcon sx={{ fontSize: 12, color: "#fff" }} />
            <Typography sx={{ fontSize: "0.72rem", color: "#fff", fontWeight: 700 }}>Reset All</Typography>
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          {["DAY_1","DAY_2","DAY_3","DAY_4","DAY_5","DAY_6","DAY_7"].map((dayKey, idx) => {
            const dayObj = retentionFollowup.find((d: any) => d.day === dayKey) ?? { day: dayKey, isCompleted: false, timestamp: null };
            return (
              <Box key={dayKey} sx={{
                flex: "1 1 0", border: "1px solid rgb(226,230,233)",
                borderRadius: "10px", p: "10px 8px", textAlign: "center",
                bgcolor: dayObj.isCompleted ? `${A.retention}0d` : "rgb(249,250,251)",
                borderColor: dayObj.isCompleted ? `${A.retention}55` : "rgb(226,230,233)",
              }}>
                <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: A.retention, mb: 0.5 }}>
                  Day {idx + 1}
                </Typography>
                <Checkbox
                  checked={!!dayObj.isCompleted}
                  size="small"
                  sx={{ color: A.retention, "&.Mui-checked": { color: A.retention }, p: 0.25 }}
                  onChange={async (e) => {
                    const isChecked = e.target.checked;
                    if (!isChecked) {
                      setRetentionFollowup((prev: any[]) =>
                        prev.map((d: any) => d.day === dayKey ? { ...d, isCompleted: false, timestamp: null } : d)
                      );
                    }
                    try {
                      const result = await cookService.updateRetentionFollowup(props.cookId, { day: dayKey, isCompleted: isChecked });
                      if (Array.isArray(result)) {
                        setRetentionFollowup(
                          !isChecked
                            ? result.map((d: any) => d.day === dayKey ? { ...d, isCompleted: false, timestamp: null } : d)
                            : result
                        );
                      }
                    } catch {
                      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: "Failed to update retention followup", type: CustomSnackbarTypes.ERROR });
                    }
                  }}
                />
                <Typography sx={{ fontSize: "0.62rem", color: colors.light.textGrey, mt: 0.5, lineHeight: 1.3 }}>
                  {dayObj.isCompleted && dayObj.timestamp ? formatDate(dayObj.timestamp) : "—"}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      {data.onboardingStatus && (
        <SkillAssessmentTable cookDetail={data as FreshLeadInterface} refreshToken={0} />
      )}
      {hubList && (
        <ResourceSelectDialog
          name="Hubs"
          list={HubsList}
          onClose={async (hub: { id: number }) => {
            if (hub) {
              const response = await cookService.updateHub(props.cookId, { hubId: hub.id });
              if (response.status === 200) { alertUser("Successful"); }
            }
            refresh();
            setHubList(false);
          }}
        />
      )}
      {photoOpen && <ImagePreviewerDialog src={data?.profilePhoto} open={photoOpen} onClose={() => setPhotoOpen(false)} />}

      <MarkNotFreeForWorkDialog
        cookId={props.cookId}
        open={markNotFreeOpen}
        onClose={() => setMarkNotFreeOpen(false)}
        onSuccess={() => { refresh(); }}
      />

      <Dialog open={freeToWorkOpen} onClose={() => { setFreeToWorkOpen(false); setFreeToWorkComment(''); }}>
        <DialogTitle>Mark Free To Work</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField label="Comment" fullWidth value={freeToWorkComment} onChange={(e) => setFreeToWorkComment(e.target.value)} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button color="error" variant="contained" onClick={() => { setFreeToWorkOpen(false); setFreeToWorkComment(''); }}>Cancel</Button>
          <Button variant="contained" onClick={() => {
            if (freeToWorkComment === '') {
              EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: "Enter Details", type: CustomSnackbarTypes.ERROR });
              return;
            }
            cookService.markFreeToWork(props.cookId, { comment: freeToWorkComment })
              .then(() => {
                EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: "Successfully marked free to work", type: CustomSnackbarTypes.SUCCESS });
                setFreeToWorkOpen(false);
                setFreeToWorkComment('');
                refresh();
              })
              .catch((err) => { console.log(err); });
          }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CookProfile;
